import APIService from '../Core/APIService';
import API_ROUTES from '../../ConfigFiles/Routes/api_routes';
import type {
    HealthBackgroundResponse,
    CreateAllergyRequest,
    UpdateAllergyRequest,
    AddAllergyNoteRequest,
    AddAllergyReactionRequest,
    CreateFamilyMemberRequest,
    AddRelativeConditionRequest,
    HereditaryBulkUpdateRequest,
} from '../../Types/schema-helpers';

export default class HealthBackgroundService extends APIService {
    /**
     * Reads health background. If no username is provided, fetches logged-in user's data (/me).
     */
    async readHealthBackground(username?: string): Promise<HealthBackgroundResponse | null> {
        try {
            const route = username
                ? API_ROUTES.fetch_patient_health_background(username)
                : API_ROUTES.fetch_own_health_background;

            return await this.get<HealthBackgroundResponse>(route);
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    // ==========================================
    // ALLERGY MUTATIONS (PROFESSIONALS ONLY)
    // ==========================================

    async addPatientAllergy(username: string, payload: CreateAllergyRequest) {
        return await this.post<HealthBackgroundResponse>(
            API_ROUTES.add_patient_allergy(username),
            payload
        );
    }

    async updatePatientAllergy(username: string, allergyId: string, payload: UpdateAllergyRequest) {
        return await this.patch<HealthBackgroundResponse>(
            API_ROUTES.update_patient_allergy(username, allergyId),
            payload
        );
    }

    async addPatientAllergyNote(username: string, allergyId: string, payload: AddAllergyNoteRequest) {
        return await this.post<HealthBackgroundResponse>(
            API_ROUTES.add_patient_allergy_note(username, allergyId),
            payload
        );
    }

    async addPatientAllergyReaction(username: string, allergyId: string, payload: AddAllergyReactionRequest) {
        return await this.post<HealthBackgroundResponse>(
            API_ROUTES.add_patient_allergy_reaction(username, allergyId),
            payload
        );
    }

    async deletePatientAllergy(username: string, allergyId: string) {
        return await this.delete<{ message: string }>(
            API_ROUTES.delete_patient_allergy(username, allergyId),
            allergyId
        );
    }

    // ==========================================
    // HEREDITARY MUTATIONS (PROFESSIONALS ONLY)
    // ==========================================

    async addPatientFamilyMember(username: string, payload: CreateFamilyMemberRequest) {
        return await this.post<HealthBackgroundResponse>(
            API_ROUTES.add_patient_family_member(username),
            payload
        );
    }

    async addPatientRelativeCondition(username: string, memberId: string, payload: AddRelativeConditionRequest) {
        return await this.post<HealthBackgroundResponse>(
            API_ROUTES.add_patient_relative_condition(username, memberId),
            payload
        );
    }

    async deletePatientFamilyMember(username: string, memberId: string) {
        return await this.delete<{ message: string }>(
            API_ROUTES.delete_patient_family_member(username, memberId),
            memberId
        );
    }

    async bulkUpdatePatientHereditary(username: string, payload: HereditaryBulkUpdateRequest) {
        return await this.put<HealthBackgroundResponse>(
            API_ROUTES.bulk_update_patient_hereditary(username),
            payload
        );
    }
}